// ═══════════════════════════════════════════════════════════════
// Impact — Module Layout with sub-navigation
// ═══════════════════════════════════════════════════════════════

import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Group, Button, Tooltip, CopyButton, ActionIcon } from '@mantine/core';
import {
  IconLayoutDashboard, IconList, IconBolt, IconClipboardCheck,
  IconMap, IconChartBar, IconFileExport, IconStack2, IconWorld, IconCheck, IconCopy,
} from '@tabler/icons-react';
import { useAuthStore } from '../../store/authStore';
import { useFilteredNav } from '../../hooks/useFilteredNav';
import type { NavItem } from '../../types/nav';
import { brand } from '../../lib/colors';
import Breadcrumbs from '../../components/ui/Breadcrumbs';

const NAV_ITEMS: NavItem[] = [
  { label: 'Dashboard', path: '/impact', icon: IconLayoutDashboard, exact: true },
  { label: 'Assessments', path: '/impact/assessments', icon: IconList },
  { label: 'Quick Capture', path: '/impact/capture', icon: IconBolt, minLevel: 'editor' },
  { label: 'Detailed', path: '/impact/detailed', icon: IconClipboardCheck, minLevel: 'editor' },
  { label: 'Bulk Entry', path: '/impact/bulk', icon: IconStack2, minLevel: 'editor' },
  { label: 'Map', path: '/impact/map', icon: IconMap },
  { label: 'Analytics', path: '/impact/analytics', icon: IconChartBar },
  { label: 'Reports', path: '/impact/reports', icon: IconFileExport },
];

export default function ImpactLayout() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { activeIncident } = useAuthStore();
  const visibleItems = useFilteredNav('impact', NAV_ITEMS);

  // Hide sub-nav on assessment detail pages (they have their own breadcrumbs)
  const isDetail = pathname.startsWith('/impact/assessment/');

  const selfReportUrl = activeIncident
    ? `${window.location.origin}/self-report/${activeIncident.id}`
    : '';

  const currentPage = NAV_ITEMS.find(item =>
    item.exact ? pathname === item.path : pathname.startsWith(item.path)
  );

  return (
    <div style={{ padding: '24px' }}>
      {!isDetail && (
        <>
        <Breadcrumbs
          items={[
            { label: 'Hub', to: '/' },
            { label: 'Impact', to: '/impact' },
            ...(currentPage && currentPage.path !== '/impact' ? [{ label: currentPage.label }] : []),
          ]}
        />
        <Group gap="xs" mb="md" style={{ borderBottom: '1px solid var(--frost)', paddingBottom: 12 }}>
          {visibleItems.map(item => {
            const active = item.exact ? pathname === item.path : pathname.startsWith(item.path);
            return (
              <Button
                key={item.path}
                variant={active ? 'filled' : 'light'}
                color={active ? brand.blurple : 'gray'}
                size="xs"
                leftSection={<item.icon size={14} />}
                onClick={() => navigate(item.path)}
              >
                {item.label}
              </Button>
            );
          })}

          {/* Public Self-Report Portal Link */}
          {activeIncident && (
            <Group gap={4} ml="auto">
              <Tooltip label="Open public damage report portal" withArrow>
                <Button
                  variant="light"
                  color="orange"
                  size="xs"
                  leftSection={<IconWorld size={14} />}
                  component="a"
                  href={selfReportUrl}
                  target="_blank"
                >
                  Public Report
                </Button>
              </Tooltip>
              <CopyButton value={selfReportUrl}>
                {({ copied, copy }) => (
                  <Tooltip label={copied ? 'Copied!' : 'Copy link'} withArrow>
                    <ActionIcon variant="light" color={copied ? 'green' : 'orange'} size="sm" onClick={copy}>
                      {copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
                    </ActionIcon>
                  </Tooltip>
                )}
              </CopyButton>
            </Group>
          )}
        </Group>
        </>
      )}
      <Outlet />
    </div>
  );
}
